import { infiniteQueryOptions } from "@tanstack/react-query";
import { IPaginatedApiResponse } from "../dataselector.interface";

export interface IFetchDataSelectPageParams {
  url: string;
  pageParam?: number;
  pageSize?: number;
  bearerToken?: string;
  queryParams?: Record<string, string | number | boolean>;
  searchTerm?: string;
  searchParamKey?: string;
}

export const dataSelectQueryKeys = {
  all: ["data-select"] as const,
  list: (params: {
    url: string;
    pageSize?: number;
    queryParams?: Record<string, string | number | boolean>;
    searchTerm?: string;
    searchParamKey?: string;
  }) =>
    [
      ...dataSelectQueryKeys.all,
      params.url,
      params.pageSize ?? 20,
      params.queryParams ?? {},
      params.searchTerm ?? "",
      params.searchParamKey ?? "",
    ] as const,
};

export const fetchDataSelectPage = async (params: IFetchDataSelectPageParams): Promise<IPaginatedApiResponse<Record<string, unknown>>> => {
  const { url, pageParam = 1, pageSize = 20, bearerToken, queryParams, searchTerm, searchParamKey } = params;

  const nextUrl = new URL(url, window.location.origin);

  nextUrl.searchParams.set("page", String(pageParam));
  nextUrl.searchParams.set("pageSize", String(pageSize));

  Object.entries(queryParams ?? {}).forEach(([key, value]) => {
    nextUrl.searchParams.set(key, String(value));
  });

  if (searchParamKey && searchTerm?.trim()) {
    nextUrl.searchParams.set(searchParamKey, searchTerm.trim());
  }

  const response = await fetch(nextUrl.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch select options. Status: ${response.status}`);
  }

  return (await response.json()) as IPaginatedApiResponse<Record<string, unknown>>;
};

export interface ICreateDataSelectInfiniteQueryOptionsParams {
  url: string;
  pageSize?: number;
  bearerToken?: string;
  queryParams?: Record<string, string | number | boolean>;
  searchTerm?: string;
  searchParamKey?: string;
  enabled?: boolean;
}

export const createDataSelectInfiniteQueryOptions = (params: ICreateDataSelectInfiniteQueryOptionsParams) => {
  const { url, pageSize = 20, bearerToken, queryParams, searchTerm, searchParamKey, enabled = true } = params;

  return infiniteQueryOptions({
    queryKey: dataSelectQueryKeys.list({
      url,
      pageSize,
      queryParams,
      searchTerm,
      searchParamKey,
    }),
    queryFn: ({ pageParam }) =>
      fetchDataSelectPage({
        url,
        pageParam: Number(pageParam ?? 1),
        pageSize,
        bearerToken,
        queryParams,
        searchTerm,
        searchParamKey,
      }),
    initialPageParam: 1,
    enabled,
    getNextPageParam: (lastPage) => {
      if (lastPage.page >= lastPage.totalPages) {
        return undefined;
      }

      return lastPage.page + 1;
    },
  });
};
